import { useCallback, useState, useEffect } from 'react';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  SignatureOutlined,
} from '@ant-design/icons';
import { AButton, ATitle, Loading, ToSignature } from '../../../components';
import { HealthCertificateReport } from '../../../shared/reports';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../services/api';
import { useConfiguration } from '../../../hooks/configuration';
import { useToast } from '../../../hooks/toast';
import { IHealthCertificate } from '../../../shared/interfaces';
import { hasAllowedModule } from '../../../shared/utils/modules';
import { ContainerPrint, HeaderContainer } from './styles';

const HealthCertificatePrint: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { configuration } = useConfiguration();
  const [loading, setLoading] = useState<boolean>(true);
  const [report, setReport] = useState<IHealthCertificate | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<string | null>(null);

  const loadHealthCertificate = useCallback(async () => {
    try {
      const healthCertificateResponse = await api.get(
        `/clients-health-certificates/${id}`,
      );

      const reportData = healthCertificateResponse.data;
      const isDigitallySigned = Boolean(
        reportData?.signature_validation_code || reportData?.signed_at,
      );

      let signedProfessionalSignature: string | null = null;
      if (isDigitallySigned && reportData?.professional_id) {
        const professionalResponse = await api.get(
          `/professionals/${reportData.professional_id}`,
        );
        signedProfessionalSignature =
          professionalResponse.data?.signature || null;
      }

      setReport(reportData);
      setValidationCode(reportData?.signature_validation_code || null);
      setSignature(isDigitallySigned ? signedProfessionalSignature : null);
      setLoading(false);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao encontrar certificado',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const renderModalSign = () => {
    if (modalSign && canUseDigitalSignature)
      return (
        <ToSignature
          open={modalSign && canUseDigitalSignature}
          close={() => setModalSign(false)}
          setSignature={setSignature}
          professional_id={report?.professional_id}
          onAuthenticated={async (authData, password) => {
            const response = await api.post('/signature-validation/sign', {
              document_type: 'HEALTH_CERTIFICATE',
              record_id: id,
              professional_id: report?.professional_id,
              password,
            });

            setSignature(
              authData.signature ||
                response.data.professional_signature ||
                null,
            );
            setValidationCode(response.data.validation_code || null);
            setSignatureMode(response.data.signature_mode || null);
            addToast({
              type: 'success',
              title: 'Documento assinado',
              description: 'Atestado assinado digitalmente com sucesso.',
            });
          }}
        />
      );
  };

  useEffect(() => {
    loadHealthCertificate();
  }, []);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode || report?.signed_at);
  const canUseDigitalSignature = hasAllowedModule(
    configuration?.allowed_modules,
    'assinatura_digital',
  );

  return (
    <>
      <HeaderContainer>
        <ATitle icon={<PrinterOutlined />}>Imprimir Atestado</ATitle>
        <ContainerPrint>
          <AButton
            type="default"
            onClick={() => navigate('/health_certificates')}
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          {!isDigitallySigned && canUseDigitalSignature && (
            <AButton
              type="dashed"
              onClick={() => setModalSign(true)}
              icon={<SignatureOutlined />}
              style={{ backgroundColor: '#33cf5c', color: '#fff' }}
            >
              Assinatura do profissional
            </AButton>
          )}
        </ContainerPrint>
      </HeaderContainer>
      {validationCode && (
        <p style={{ marginTop: 8, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}
      {signatureMode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Modo da assinatura:{' '}
          {signatureMode === 'A1' ? 'Assinado com A1' : 'Assinatura interna'}
        </p>
      )}
      <HealthCertificateReport
        report={report}
        signature={signature}
        validationCode={validationCode || report?.signature_validation_code}
      />
      {renderModalSign()}
    </>
  );
};

export default HealthCertificatePrint;
